import { useState, useEffect, useRef, Fragment } from 'react';
import '../styles/assessment-layout.css';

export interface Manifestacao {
    campo: string;
    descricao: string;
    nivel?: number;
}

export interface Bloco {
    titulo?: string;
    grupos: Manifestacao[][];
}

interface Props {
    blocos: Bloco[];
    answers: { [key: string]: number };
    onAnswer: (campo: string, valor: number) => void;
    onSubmit: () => void;
    onExit: () => void;
}

const OPCOES = [
    'Não observada',
    'Raramente observada',
    'Observada com certa frequência',
    'Marcante',
    'Fortemente incorporada',
];

import { useIsMobile } from '../hooks/useIsMobile';
export { useIsMobile };

export function AssessmentLayout({ blocos, answers, onAnswer, onSubmit, onExit }: Props) {
    const isMobile = useIsMobile();
    const [atual, setAtual] = useState(0);
    const posicionado = useRef(false);
    const avancoTimer = useRef<number>();

    // Estado de acessibilidade (persistido)
    const [largeText, setLargeText] = useState(() => localStorage.getItem('a11y_large') === 'true');
    const [highContrast, setHighContrast] = useState(() => localStorage.getItem('a11y_contrast') === 'true');

    // Estado do tema claro/escuro (persistido)
    const [theme, setTheme] = useState<'light' | 'dark'>(() => {
        const saved = localStorage.getItem('capj_theme');
        return saved === 'dark' ? 'dark' : 'light';
    });

    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('capj_theme', theme);
    }, [theme]);

    const toggleTheme = () => {
        setTheme(prev => (prev === 'dark' ? 'light' : 'dark'));
    };

    const toggleLargeText = () => {
        setLargeText(prev => {
            const next = !prev;
            localStorage.setItem('a11y_large', String(next));
            return next;
        });
    };

    const toggleHighContrast = () => {
        setHighContrast(prev => {
            const next = !prev;
            localStorage.setItem('a11y_contrast', String(next));
            return next;
        });
    };

    // Sequência linear das perguntas (ordem da tela), com o título do atributo de cada uma
    const lista = blocos.flatMap(b => b.grupos.flat().map(m => ({ m, titulo: b.titulo })));
    const indice = new Map(lista.map((item, i) => [item.m.campo, i]));

    const respondida = (m: Manifestacao) => (answers[m.campo] || 0) > 0;
    const total = lista.length;
    const feitas = lista.filter(item => respondida(item.m)).length;
    const pendentes = total - feitas;
    const primeiraPendente = lista.findIndex(item => !respondida(item.m));

    // Ao voltar para o assessment, o celular abre na primeira pergunta ainda sem resposta
    useEffect(() => {
        if (posicionado.current || Object.keys(answers).length === 0) return;
        posicionado.current = true;
        if (primeiraPendente > 0) setAtual(primeiraPendente);
    }, [answers, primeiraPendente]);

    useEffect(() => () => window.clearTimeout(avancoTimer.current), []);

    const responder = (campo: string, valor: number) => {
        const novo = answers[campo] === valor ? 0 : valor;
        onAnswer(campo, novo);
        const idx = indice.get(campo) ?? 0;
        if (isMobile && novo > 0 && idx < total - 1) {
            window.clearTimeout(avancoTimer.current);
            avancoTimer.current = window.setTimeout(() => setAtual(idx + 1), 400);
        }
    };

    // Suporte a atalhos de teclado: teclas 1 a 5 respondem a pergunta atual
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement)?.tagName)) return;
            if (['1', '2', '3', '4', '5'].includes(e.key)) {
                const val = parseInt(e.key, 10);
                const targetManifestacao = isMobile ? lista[atual]?.m : null;
                if (targetManifestacao) {
                    responder(targetManifestacao.campo, val);
                }
            } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
                if (isMobile && atual < total - 1) {
                    setAtual(prev => prev + 1);
                }
            } else if (e.key === 'ArrowLeft') {
                if (isMobile && atual > 0) {
                    setAtual(prev => prev - 1);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [atual, isMobile, lista, answers]);

    const irParaPendente = () => {
        if (primeiraPendente < 0) return;
        if (isMobile) {
            setAtual(primeiraPendente);
        } else {
            document.getElementById(`av2-q${primeiraPendente}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const tentarSubmeter = () => {
        const pendentesItems = lista
            .map((item, idx) => ({ idx, num: idx + 1, respondida: respondida(item.m) }))
            .filter(item => !item.respondida);

        if (pendentesItems.length > 0) {
            const numeros = pendentesItems.map(p => p.num);
            const numerosFormatados = numeros.length <= 15
                ? numeros.join(', ')
                : `${numeros.slice(0, 15).join(', ')}... e mais ${numeros.length - 15}`;

            alert(
                `Não é possível enviar o assessment incompleto.\n\n` +
                `Você ainda precisa responder ${pendentesItems.length} pergunta(s):\n` +
                `Pergunta(s): ${numerosFormatados}\n\n` +
                `O questionário só pode ser enviado com 100% das respostas.\n` +
                `Vamos direcionar você para a primeira pergunta pendente (Pergunta ${pendentesItems[0].num}).`
            );
            if (isMobile) {
                setAtual(pendentesItems[0].idx);
            } else {
                document.getElementById(`av2-q${pendentesItems[0].idx}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        onSubmit();
    };

    const pergunta = (m: Manifestacao) => {
        const idx = indice.get(m.campo) ?? 0;
        const feita = respondida(m);
        return (
            <div key={m.campo} id={`av2-q${idx}`} className={`av2-cell${feita ? ' av2-done' : ''}`}>
                <div className="av2-q-head">
                    <span className="av2-q-num">PERGUNTA {idx + 1} DE {total}</span>
                    <span className="av2-q-state">{feita ? '✓ Respondida' : 'Pendente'}</span>
                </div>
                <p className="av2-q-text">{m.descricao}</p>
                <div className="av2-scale" role="group" aria-label="Escala de avaliação">
                    {OPCOES.map((label, i) => {
                        const valor = i + 1;
                        const isSelected = answers[m.campo] === valor;
                        return (
                            <button 
                                key={valor} 
                                type="button" 
                                className="av2-opt" 
                                aria-pressed={isSelected}
                                onClick={() => responder(m.campo, valor)}
                                title={label}
                            >
                                <span className="av2-l">{label}</span>
                                {isSelected && (
                                    <span className="av2-opt-check" aria-hidden="true">✓</span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    };

    const cartao = (grupo: Manifestacao[], key: string) => {
        const destaque = !isMobile && grupo.some(m => indice.get(m.campo) === primeiraPendente);
        return (
            <section key={key} className={`av2-card${grupo.length > 1 ? ' av2-pair' : ''}${destaque ? ' av2-current' : ''}`}>
                {grupo.map(pergunta)}
            </section>
        );
    };

    const intro = isMobile ? (
        <details className="av2-intro av2-intro-collapsible">
            <summary className="av2-intro-summary">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '6px', flexShrink: 0 }}>
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 16v-4" />
                    <path d="M12 8h.01" />
                </svg>
                <span><b>Instruções</b> (toque para ler)</span>
            </summary>
            <p style={{ marginTop: '6px' }}>
                Para cada afirmação, indique com que intensidade e frequência você observa esse
                comportamento na sua esfera de influência. Não há resposta certa ou errada: vale a
                percepção dominante do grupo.
            </p>
            <div className="av2-legend">
                {OPCOES.map((label, i) => <div key={i}>{label}</div>)}
            </div>
        </details>
    ) : (
        <div className="av2-intro">
            <h1>Como responder</h1>
            <p>
                Para cada afirmação, indique com que intensidade e frequência você observa esse
                comportamento na sua esfera de influência. Não há resposta certa ou errada: vale a
                percepção dominante do grupo.
            </p>
            <div className="av2-legend">
                {OPCOES.map((label, i) => <div key={i}>{label}</div>)}
            </div>
        </div>
    );

    const temPares = blocos.some(b => b.grupos.some(g => g.length > 1));
    const itemAtual = lista[atual];
    const itemAtualRespondido = itemAtual ? respondida(itemAtual.m) : false;

    return (
        <div 
            data-theme={theme}
            className={`av2-page${temPares ? ' av2-wide' : ''}${theme === 'dark' ? ' theme-dark' : ''}${largeText ? ' a11y-large' : ''}${highContrast ? ' a11y-contrast' : ''}`}
        >
            <header className="av2-header">
                <div className="av2-row">
                    <div className="av2-brand-group">
                        <span className="av2-brand">cap jota</span>
                        <span className="av2-subbrand">BMI Assessment CAP Jota</span>
                    </div>

                    <div className="av2-header-meta">
                        <span className="av2-progress-compact" aria-live="polite" title={`Pergunta ${atual + 1} de ${total}`}>
                            {atual + 1}/{total}
                        </span>

                        {/* Toolbar de Acessibilidade e Tema */}
                        <div className="av2-a11y-tools" role="toolbar" aria-label="Ferramentas de Acessibilidade e Tema">
                            <button 
                                type="button" 
                                className={`av2-a11y-btn${largeText ? ' active' : ''}`}
                                onClick={toggleLargeText}
                                title={largeText ? 'Tamanho de texto normal' : 'Tamanho de texto maior'}
                                aria-label={largeText ? 'Tamanho de texto normal' : 'Tamanho de texto maior'}
                                aria-pressed={largeText}
                            >
                                <span className="av2-a11y-icon av2-a11y-icon-font" aria-hidden="true">A+</span>
                                <span className="av2-a11y-text">{largeText ? 'Texto Normal' : 'Texto Maior'}</span>
                            </button>
                            <button 
                                type="button" 
                                className={`av2-a11y-btn${highContrast ? ' active' : ''}`}
                                onClick={toggleHighContrast}
                                title="Alternar modo de alto contraste"
                                aria-label="Alternar modo de alto contraste"
                                aria-pressed={highContrast}
                            >
                                <svg className="av2-a11y-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <circle cx="12" cy="12" r="9" />
                                    <path d="M12 3a9 9 0 0 1 0 18z" fill="currentColor" />
                                </svg>
                                <span className="av2-a11y-text">Contraste</span>
                            </button>
                            <button 
                                type="button" 
                                className={`av2-a11y-btn${theme === 'dark' ? ' active' : ''}`}
                                onClick={toggleTheme}
                                title={theme === 'dark' ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
                                aria-label={theme === 'dark' ? 'Mudar para tema claro' : 'Mudar para tema escuro'}
                                aria-pressed={theme === 'dark'}
                            >
                                {theme === 'dark' ? (
                                    <svg className="av2-a11y-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <circle cx="12" cy="12" r="4" />
                                        <path d="M12 2v2" />
                                        <path d="M12 20v2" />
                                        <path d="m4.93 4.93 1.41 1.41" />
                                        <path d="m17.66 17.66 1.41 1.41" />
                                        <path d="M2 12h2" />
                                        <path d="M20 12h2" />
                                        <path d="m6.34 17.66-1.41 1.41" />
                                        <path d="m19.07 4.93-1.41 1.41" />
                                    </svg>
                                ) : (
                                    <svg className="av2-a11y-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
                                    </svg>
                                )}
                                <span className="av2-a11y-text">{theme === 'dark' ? 'Tema Claro' : 'Tema Escuro'}</span>
                            </button>
                        </div>
                    </div>

                    <div className="av2-progress">
                        <div className="av2-progress-top">
                            <span>{feitas} de {total} respondidas</span>
                        </div>
                        <div className="av2-bar"><i style={{ width: `${total ? (feitas / total) * 100 : 0}%` }} /></div>
                    </div>
                </div>

                {/* Barra de progresso ultra fina na borda inferior do header no Mobile */}
                <div className="av2-mobile-bar" aria-hidden="true">
                    <i style={{ width: `${total ? (feitas / total) * 100 : 0}%` }} />
                </div>
            </header>

            <main className="av2-main">
                {isMobile ? (
                    <>
                        {atual === 0 && intro}
                        {itemAtual?.titulo && <p className="av2-eyebrow">{itemAtual.titulo}</p>}
                        {itemAtual && cartao([itemAtual.m], itemAtual.m.campo)}
                    </>
                ) : (
                    <>
                        {intro}
                        {blocos.map((b, bi) => (
                            <Fragment key={bi}>
                                {b.titulo && <h2 className="av2-block-title">{b.titulo}</h2>}
                                {b.grupos.map((g, gi) => cartao(g, `${bi}-${gi}`))}
                            </Fragment>
                        ))}
                    </>
                )}
            </main>

            <div className="av2-actions">
                <div className="av2-row">
                    {isMobile ? (
                        <div className="av2-group">
                            <div className="av2-mobile-btn-row">
                                <button 
                                    type="button" 
                                    className="av2-btn av2-btn-nav-prev" 
                                    disabled={atual === 0}
                                    onClick={() => {
                                        window.clearTimeout(avancoTimer.current);
                                        setAtual(prev => Math.max(0, prev - 1));
                                    }}
                                >
                                    ← Anterior
                                </button>
                                {atual < total - 1 ? (
                                    <button 
                                        type="button" 
                                        className="av2-btn av2-btn-nav-next"
                                        onClick={() => {
                                            window.clearTimeout(avancoTimer.current);
                                            setAtual(prev => Math.min(total - 1, prev + 1));
                                        }}
                                    >
                                        Próxima →
                                    </button>
                                ) : (
                                     <button 
                                        type="button" 
                                        className="av2-btn av2-btn-nav-submit"
                                        onClick={() => {
                                            window.clearTimeout(avancoTimer.current);
                                            tentarSubmeter();
                                        }}
                                    >
                                        Finalizar e Submeter ✓
                                    </button>
                                )}
                            </div>
                            <div className="av2-mobile-nav">
                                <span className="av2-hint">
                                    {feitas} respondidas • {pendentes ? `${pendentes} pendentes` : 'Todas concluídas ✓'}
                                </span>
                                <button type="button" className="av2-btn-exit-mobile" onClick={onExit}>
                                    Sair
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="av2-group">
                                <button type="button" className="av2-btn av2-btn-ghost" disabled={pendentes === 0}
                                    onClick={irParaPendente}>Ir para a próxima pendente ↓</button>
                                <span className={`av2-hint${pendentes ? ' av2-warn' : ''}`}>
                                    {pendentes ? `${pendentes} pendentes` : 'Todas respondidas'}
                                </span>
                            </div>
                            <div className="av2-group">
                                <button type="button" className="av2-btn av2-btn-exit" onClick={onExit}>Sair</button>
                                <button type="button" className="av2-btn av2-btn-primary" onClick={tentarSubmeter}>Submeter avaliação</button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
